/**
 * Entity search logic for app/user autocomplete within CEL function parameters.
 *
 * Detects when the cursor is inside specific function calls (e.g., `HasApp()`, `GetByID()`)
 * and provides autocomplete suggestions by searching the app/user APIs.
 *
 * @module cel/entitySearch
 */

import type monaco from 'monaco-editor'
import { SearchAppsRequest } from 'pbts/c1/api/app/v1/app'
import type { AppSearchClient } from 'pbts/c1/api/app/v1/app.client'
import { SearchUsersRequest } from 'pbts/c1/api/user/v1/user'
import type { UserSearchClient } from 'pbts/c1/api/user/v1/user.client'

// Regex patterns to detect function calls that need entity search
const hasAppPattern = /c1.user.v1.HasApp\((?:(?<name>[^)]+))?/
const hasUserPattern = /c1.directory.users.v1.GetByID\((?:(?<name>[^)]+))?/

/**
 * Context for entity search operations.
 */
export interface EntitySearchContext {
    /** Type of entity to search for */
    searchType: 'app' | 'user' | undefined
    /** Search query extracted from the expression */
    query: string
    /** Quote character being used (' or ") */
    quoteMark: string
    /** Column offset where the replacement should start */
    offset: number
}

/**
 * Parse the current line to determine if the cursor is in an entity search context.
 *
 * Detects patterns like `c1.user.v1.HasApp(` or `c1.directory.users.v1.GetByID(`
 * and extracts the search query from partially typed arguments.
 *
 * @param lineUpToPosition - The line content from start to cursor position
 * @param column - Current cursor column (1-indexed)
 * @returns Entity search context with search type, query, and offset
 *
 * @example
 * ```ts
 * const ctx = parseEntitySearchContext('c1.user.v1.HasApp("sales', 25)
 * // Returns: { searchType: 'app', query: 'sales', quoteMark: '"', offset: 19 }
 * ```
 */
export const parseEntitySearchContext = (
    lineUpToPosition: string,
    column: number,
): EntitySearchContext => {
    let searchType: 'app' | 'user' | undefined
    let query = ''
    let quoteMark = '"'
    let offset = column

    const hasAppResult = hasAppPattern.exec(lineUpToPosition)
    const hasUserResult = hasUserPattern.exec(lineUpToPosition)

    if (hasUserResult && hasAppResult) {
        searchType = hasUserResult.index > hasAppResult.index ? 'user' : 'app'
        query = searchType === 'app' ? hasAppResult.groups?.name ?? '' : hasUserResult.groups?.name ?? ''
    } else if (hasAppResult) {
        searchType = 'app'
        query = hasAppResult.groups?.name ?? ''
        offset = hasAppResult.index + 'c1.user.v1.HasApp('.length
    } else if (hasUserResult) {
        searchType = 'user'
        query = hasUserResult.groups?.name ?? ''
        offset = hasUserResult.index + 'c1.directory.users.v1.GetByID('.length
    }

    if (query.includes("'")) {
        quoteMark = "'"
    }

    return { searchType, query, quoteMark, offset }
}

/**
 * Fetch entity suggestions from the app or user search APIs.
 *
 * Queries the appropriate API based on the search context and returns
 * Monaco completion items with the entity ID as insert text.
 *
 * @param context - Entity search context from `parseEntitySearchContext`
 * @param position - Current cursor position in the editor
 * @param appSearchClient - RPC client for app search
 * @param userSearchClient - RPC client for user search
 * @param monacoModule - Monaco editor module for CompletionItemKind
 * @returns Array of completion items with entity suggestions
 *
 * @example
 * ```ts
 * const context = parseEntitySearchContext(line, column)
 * if (context.searchType) {
 *   const suggestions = await fetchEntitySuggestions(
 *     context, position, appClient, userClient, monaco
 *   )
 *   // suggestions = [{ label: 'Sales App', insertText: '("app-123")', ... }]
 * }
 * ```
 */
export const fetchEntitySuggestions = async (
    context: EntitySearchContext,
    position: monaco.Position,
    appSearchClient: AppSearchClient,
    userSearchClient: UserSearchClient,
    monacoModule: typeof monaco,
): Promise<monaco.languages.CompletionItem[]> => {
    const suggestions: monaco.languages.CompletionItem[] = []
    const { searchType, query, quoteMark, offset } = context

    const range = {
        startColumn: offset,
        startLineNumber: position.lineNumber,
        endColumn: position.column,
        endLineNumber: position.lineNumber,
    }

    switch (searchType) {
        case 'user': {
            const { response } = await userSearchClient.search(SearchUsersRequest.create({
                query: query.replaceAll(/['"()]/g, ''),
                pageSize: 100,
            }))
            response.list?.forEach((userView) => {
                if (!userView.user) {
                    return
                }
                const user = userView.user
                suggestions.push({
                    label: `${user.displayName} (${user.email})`,
                    sortText: user.email,
                    detail: user.id,
                    kind: monacoModule.languages.CompletionItemKind.Text,
                    insertText: `(${quoteMark}${user.id}${quoteMark})\n// user id ${user.id} is ${user.email}`,
                    range,
                })
            })
            break
        }
        case 'app': {
            const { response } = await appSearchClient.search(SearchAppsRequest.create({
                query: query.replaceAll(/^['"()]/g, ''),
                pageSize: 100,
            }))
            response.list?.forEach((app) => {
                suggestions.push({
                    label: app.displayName,
                    detail: app.id,
                    documentation: app.description,
                    kind: monacoModule.languages.CompletionItemKind.Text,
                    insertText: `(subject, ${quoteMark}${app.id}${quoteMark})\n// app id ${app.id} is ${app.displayName}`,
                    range,
                })
            })
            break
        }
        default:
            // No entity search for this context
            break
    }

    return suggestions
}
